import React, { useEffect, useState } from "react";
import "./Pagination.css";

import { RxDoubleArrowLeft, RxDoubleArrowRight } from "react-icons/rx";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import SearchableSelect from "./components/common/SearchableSelect";

const Pagination = ({
  className = "",
  showPerPage = 20,
  total = 0,
  onPaginationChange,
  handleShowPerPageChange,
  resetKey = "",
  translate,
}) => {
  const text = (key, fallback) => {
    const translatedValue = translate?.(key);
    return translatedValue && translatedValue !== key
      ? translatedValue
      : fallback;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = Number(showPerPage) || 20;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const isFirstDisabled = currentPage <= 1;
  const isLastDisabled = currentPage >= totalPages || total === 0;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey]);

  useEffect(() => {
    const startIndex = total === 0 ? 0 : (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    onPaginationChange(startIndex, endIndex);
  }, [currentPage, itemsPerPage, onPaginationChange, total]);

  const onButtonclick = (type) => {
    if (type === "prev" && !isFirstDisabled) {
      setCurrentPage((page) => page - 1);
    } else if (type === "next" && !isLastDisabled) {
      setCurrentPage((page) => page + 1);
    } else if (type === "first" && !isFirstDisabled) {
      setCurrentPage(1);
    } else if (type === "last" && !isLastDisabled) {
      setCurrentPage(totalPages);
    }
  };

  const getPageRangeText = () => {
    if (total === 0) {
      return "0 to 0 of 0";
    }

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(startIndex + itemsPerPage - 1, total);
    return `${startIndex} to ${endIndex} of ${total}`;
  };

  const getVisiblePageNumbers = () => {
    const visiblePageCount = Math.min(5, totalPages);
    let firstVisiblePage = Math.max(
      1,
      currentPage - Math.floor(visiblePageCount / 2)
    );
    const lastVisiblePage = Math.min(
      totalPages,
      firstVisiblePage + visiblePageCount - 1
    );

    firstVisiblePage = Math.max(1, lastVisiblePage - visiblePageCount + 1);

    return Array.from(
      { length: lastVisiblePage - firstVisiblePage + 1 },
      (_, index) => firstVisiblePage + index
    );
  };

  const onPerPageChange = (event) => {
    setCurrentPage(1);
    handleShowPerPageChange?.(event);
  };

  return (
    <nav
      aria-label={text(
        "pagination_label",
        "Case property register pagination"
      )}
      className={`paginations ${className}`.trim()}
    >
      <div className="pagination-body">
        <div className="pagination-left-side">
          <div className="page-range">
            {text("showing", "Showing")} {getPageRangeText()}{" "}
            {text("records", "records")}
          </div>
        </div>
        <div className="pagination-right-side">
          <span className="itempage">
            {text("rows_per_page", "Rows per page")}
          </span>
          <div className="dropdown">
            <div className="dropdown-option">
              <SearchableSelect
                name="dropdown"
                onChange={onPerPageChange}
                searchable={false}
                value={itemsPerPage}
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </SearchableSelect>
            </div>
          </div>
          <div className="arrow-number">
            <button
              aria-label={text("go_to_first_page", "Go to first page")}
              className="arrow1"
              onClick={() => onButtonclick("first")}
              disabled={isFirstDisabled}
              title={text("first_page", "First Page")}
              type="button"
            >
              <RxDoubleArrowLeft />
            </button>
            <button
              aria-label={text(
                "go_to_previous_page",
                "Go to previous page"
              )}
              className="arrow2"
              onClick={() => onButtonclick("prev")}
              disabled={isFirstDisabled}
              title={text("previous_page", "Previous Page")}
              type="button"
            >
              <IoIosArrowBack />
            </button>
            <div
              aria-label={`${text("page", "Page")} ${currentPage} ${text(
                "of",
                "of"
              )} ${totalPages}`}
              className="pagination-page-numbers"
            >
              {getVisiblePageNumbers().map((pageNumber) => (
                <button
                  aria-current={
                    pageNumber === currentPage ? "page" : undefined
                  }
                  aria-label={`${text("go_to_page", "Go to page")} ${pageNumber}`}
                  className={`pagination-page-button${
                    pageNumber === currentPage
                      ? " pagination-page-button--active"
                      : ""
                  }`}
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  type="button"
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              aria-label={text("go_to_next_page", "Go to next page")}
              className="arrow3"
              onClick={() => onButtonclick("next")}
              disabled={isLastDisabled}
              title={text("next_page", "Next Page")}
              type="button"
            >
              <IoIosArrowForward />
            </button>
            <button
              aria-label={text("go_to_last_page", "Go to last page")}
              className="arrow4"
              onClick={() => onButtonclick("last")}
              disabled={isLastDisabled}
              title={text("last_page", "Last Page")}
              type="button"
            >
              <RxDoubleArrowRight />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Pagination;
